import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import { supabase } from "@/integrations/supabase/client";

interface CurrencyRate {
  currency_code: string;
  currency_name: string;
  symbol: string;
  rate_to_lkr: number;
}

interface Language {
  code: string;
  name: string;
  nativeName: string;
  flag: string;
}

interface PreferencesContextType {
  currency: string;
  language: string;
  currencies: CurrencyRate[];
  languages: Language[];
  setCurrency: (code: string) => void;
  setLanguage: (code: string) => void;
  hasSetPreferences: boolean;
  setHasSetPreferences: (value: boolean) => void;
  formatPrice: (priceInLKR: number) => string;
  getCurrencySymbol: () => string;
  convertPrice: (priceInLKR: number) => number;
}

const languages: Language[] = [
  { code: "en", name: "English", nativeName: "English", flag: "🇬🇧" },
  { code: "ja", name: "Japanese", nativeName: "日本語", flag: "🇯🇵" },
  { code: "ar", name: "Arabic", nativeName: "العربية", flag: "🇸🇦" },
  { code: "es", name: "Spanish", nativeName: "Español", flag: "🇪🇸" },
  { code: "fr", name: "French", nativeName: "Français", flag: "🇫🇷" },
];

const PreferencesContext = createContext<PreferencesContextType | undefined>(undefined);

export function PreferencesProvider({ children }: { children: ReactNode }) {
  const [currency, setCurrencyState] = useState<string>("LKR");
  const [language, setLanguageState] = useState<string>("en");
  const [currencies, setCurrencies] = useState<CurrencyRate[]>([]);
  const [hasSetPreferences, setHasSetPreferencesState] = useState<boolean>(true);

  useEffect(() => {
    // Check if preferences have been set before
    const savedPrefs = localStorage.getItem("site_preferences");
    if (savedPrefs) {
      const prefs = JSON.parse(savedPrefs);
      setCurrencyState(prefs.currency || "LKR");
      setLanguageState(prefs.language || "en");
      setHasSetPreferencesState(true);
    } else {
      setHasSetPreferencesState(false);
    }

    // Fetch currencies
    fetchCurrencies();
  }, []);

  const fetchCurrencies = async () => {
    const { data } = await supabase
      .from("currency_rates")
      .select("currency_code, currency_name, symbol, rate_to_lkr")
      .eq("is_active", true)
      .order("display_order", { ascending: true });

    if (data) {
      setCurrencies(data);
    }
  };

  const setCurrency = (code: string) => {
    setCurrencyState(code);
    savePreferences(code, language);
  };

  const setLanguage = (code: string) => {
    setLanguageState(code);
    savePreferences(currency, code);
    
    // Trigger Google Translate
    const select = document.querySelector(".goog-te-combo") as HTMLSelectElement | null;
    if (select) {
      select.value = code;
      select.dispatchEvent(new Event("change"));
    }
  };

  const setHasSetPreferences = (value: boolean) => {
    setHasSetPreferencesState(value);
    if (value) {
      savePreferences(currency, language);
    }
  };

  const savePreferences = (curr: string, lang: string) => {
    localStorage.setItem(
      "site_preferences",
      JSON.stringify({ currency: curr, language: lang })
    );
  };

  const getCurrencyRate = () => {
    return currencies.find((c) => c.currency_code === currency);
  };

  const getCurrencySymbol = () => {
    const rate = getCurrencyRate();
    return rate?.symbol || "Rs";
  };

  const convertPrice = (priceInLKR: number): number => {
    const rate = getCurrencyRate();
    if (!rate || rate.currency_code === "LKR") {
      return priceInLKR;
    }
    return priceInLKR / rate.rate_to_lkr;
  };

  const formatPrice = (priceInLKR: number): string => {
    const converted = convertPrice(priceInLKR);
    const symbol = getCurrencySymbol();
    
    return `${symbol} ${new Intl.NumberFormat("en-US", {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(converted)}`;
  };

  return (
    <PreferencesContext.Provider
      value={{
        currency,
        language,
        currencies,
        languages,
        setCurrency,
        setLanguage,
        hasSetPreferences,
        setHasSetPreferences,
        formatPrice,
        getCurrencySymbol,
        convertPrice,
      }}
    >
      {children}
    </PreferencesContext.Provider>
  );
}

export function usePreferences() {
  const context = useContext(PreferencesContext);
  if (context === undefined) {
    throw new Error("usePreferences must be used within a PreferencesProvider");
  }
  return context;
}
